import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en/translation.json';

// Arabic is the only supported RTL language today -- kept as a plain list
// (rather than a lookup baked into a UI library) so LanguageSwitcher and the
// dir-setting effect in App.js both read from one place.
export const RTL_LANGUAGES = ['ar'];

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'sw', label: 'Kiswahili' },
  { code: 'fr', label: 'Français' },
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Only English ships in the main bundle. The other three are ~17KB of JSON
    // each, a visitor reads exactly one of them, and all four used to load
    // before anything could paint. ensureLanguage() below registers the rest.
    resources: {
      en: { translation: en },
    },
    // Deliberately NOT partialBundledLanguages: that flag tells i18next to ask a
    // backend connector for the languages it does not have, and there is no
    // backend here -- it would just log "No backend was added" on every non-English
    // visit. addResourceBundle writes straight into the resource store, which t()
    // reads live, so late-arriving translations work without it.
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'acymart_language',
    },
  });

// One entry per lazily-fetched language. Keep in step with SUPPORTED_LANGUAGES
// above: a code listed there but missing here simply never loads and falls back
// to English, which is silent and easy to miss.
const LAZY_TRANSLATIONS = {
  ar: () => import('./locales/ar/translation.json'),
  sw: () => import('./locales/sw/translation.json'),
  fr: () => import('./locales/fr/translation.json'),
};

// Resolves once `lng`'s translations are registered with i18next. Safe to call
// with anything -- an unknown code, a region variant like 'fr-CA', or a language
// already loaded -- and it deliberately never rejects: a translation that fails
// to arrive should degrade to the English fallback, not break the page.
export async function ensureLanguage(lng) {
  const base = String(lng || '').split('-')[0];
  const load = LAZY_TRANSLATIONS[base];
  if (!load || i18n.hasResourceBundle(base, 'translation')) return;
  try {
    const mod = await load();
    i18n.addResourceBundle(base, 'translation', mod.default, true, true);
  } catch {
    /* chunk fetch failed -- i18next falls back to English on its own */
  }
}

// The one way the app should change language: fetch the bundle first, so the UI
// never renders a frame of English on its way to the language just chosen.
export async function changeLanguage(lng) {
  await ensureLanguage(lng);
  return i18n.changeLanguage(lng);
}

// The detector reads localStorage/navigator synchronously during init above, so
// a returning French or Arabic visitor already has i18n.language set to a
// language that is no longer bundled. index.js waits on this before its first
// render; it resolves immediately for English and never rejects.
export const i18nReady = ensureLanguage(i18n.language);

export default i18n;
